import type { ExperimentConfig, ExperimentStep, ExperimentPart } from '@/types';

// ============================================================================
// EXPERIMENT 3: Qualitative Analysis Part 2 - Anions
// ============================================================================

// Color codes for anion analysis
export const anionColors = {
  // Initial solution colors
  co3Initial: '#F5F5F5',      // Colorless
  clInitial: '#F5F5F5',       // Colorless
  iInitial: '#FFFDE7',        // Colorless to pale yellow
  so4Initial: '#F5F5F5',      // Colorless
  // Positive test result colors
  baOH2Cloudy: '#E0E0E0',     // Cloudy white - BaCO3 in drop
  agClPrecipitate: '#FAFAFA', // White - AgCl
  agIPrecipitate: '#FFEB3B',  // Yellow - AgI
  iAqueous: '#8D6E63',        // Reddish-brown - I2 in water
  iOrganic: '#7B1FA2',        // Violet/purple - I2 in organic layer
  baSO4Precipitate: '#ECEFF1', // White - BaSO4
  // Reagent colors
  h2so4: '#F5F5F5',
  hcl: '#F5F5F5',
  agno3: '#F5F5F5',
  nh4oh: '#F5F5F5',
  hno3: '#F5F5F5',
  kno2: '#F5F5F5',
  aceticAcid: '#F5F5F5',
  methyleneChloride: '#F5F5F5',
  baOH2: '#F5F5F5',
  // UI colors
  waterBath: '#81D4FA',
  organicLayer: '#CE93D8',
};

// Anion data
export interface AnionData {
  id: string;
  symbol: string;
  name: string;
  charge: string;
  initialColor: string;
  confirmationTest: string;
  positiveResult: string;
}

export const anionData: Record<string, AnionData> = {
  co3: {
    id: 'co3',
    symbol: 'CO3^2-',
    name: 'Carbonate',
    charge: '2-',
    initialColor: '#F5F5F5',
    confirmationTest: 'H2SO4 + Ba(OH)2 drop test',
    positiveResult: 'Effervescence + cloudy drop',
  },
  cl: {
    id: 'cl',
    symbol: 'Cl-',
    name: 'Chloride',
    charge: '-',
    initialColor: '#F5F5F5',
    confirmationTest: 'AgNO3 + NH4OH + HNO3',
    positiveResult: 'White precipitate that redissolves and reappears',
  },
  i: {
    id: 'i',
    symbol: 'I-',
    name: 'Iodide',
    charge: '-',
    initialColor: '#FFFDE7',
    confirmationTest: 'KNO2 + CH2Cl2 layer test',
    positiveResult: 'Violet color in organic layer',
  },
  so4: {
    id: 'so4',
    symbol: 'SO4^2-',
    name: 'Sulfate',
    charge: '2-',
    initialColor: '#F5F5F5',
    confirmationTest: 'BaCl2-CaCl2 + HCl (heat)',
    positiveResult: 'White precipitate that does NOT dissolve in HCl',
  },
};

// Part 1: Introduction and Setup
const part1Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Safety Check',
    shortDescription: 'Review safety procedures for anion analysis',
    handler: 'handleSafetyAcknowledgement',
    infoContent: {
      title: 'Lab Safety',
      content: `Safety precautions for anion analysis:
- Wear safety goggles at all times
- Concentrated H2SO4 is very corrosive - handle with extreme care
- Work in fume hood when using H2SO4
- Methylene chloride is volatile - avoid inhalation
- Ba(OH)2 is toxic - do not ingest
- Wash hands thoroughly after the experiment`,
      whatHappens: 'Safety protocols are reviewed for handling concentrated sulfuric acid, volatile methylene chloride, and toxic barium hydroxide before beginning anion analysis.',
      observe: [
        'Confirm the fume hood is operational before using H2SO4',
        'Locate all reagents and ensure proper labeling',
        'Note that methylene chloride is denser than water and forms a lower layer',
      ],
    },
  },
  {
    id: 2,
    title: 'Understand Anions',
    shortDescription: 'Learn about the four anions to be tested',
    handler: 'handleLearnAnions',
    infoContent: {
      title: 'What is an Anion?',
      content: `An anion is a negatively charged ion formed when an atom gains one or more electrons. Non-metals typically form anions because they have high electron affinity. For example:
- Carbonate (CO3^2-) has gained 2 electrons
- Chloride (Cl-) has gained 1 electron
- Iodide (I-) has gained 1 electron
- Sulfate (SO4^2-) has gained 2 electrons`,
      chemicalEquation: 'Cl + e- -> Cl-',
      whatHappens: 'The four anions are introduced: negatively charged ions formed by electron gain, each with a specific confirmation test.',
      observe: [
        'All four anion solutions appear colorless or nearly colorless',
        'Iodide solution may have a very faint pale yellow tint',
        'Each anion requires a different confirmation test to identify',
      ],
    },
  },
  {
    id: 3,
    title: 'Prepare Solutions',
    shortDescription: 'Set up labeled test tubes with anion solutions',
    handler: 'handlePrepareSolutions',
    validation: {
      requires: ['safetyAcknowledged'],
    },
  },
];

// Part 2: Carbonate Confirmation Tests
const part2Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add CO3^2- Solution',
    shortDescription: 'Add 3 mL carbonate solution to a clean test tube',
    handler: 'handleAddCO3Solution',
    infoContent: {
      title: 'Carbonate Solution',
      content: `Carbonate solutions are colorless. The carbonate ion reacts vigorously with acids to produce carbon dioxide gas.`,
      whatHappens: 'A measured amount of colorless carbonate solution is placed in a clean test tube for acid-reaction testing.',
      observe: [
        'The carbonate solution is clear and colorless',
        'Ensure the test tube is clean and dry before adding solution',
        'Have concentrated H2SO4 ready in the fume hood',
      ],
    },
  },
  {
    id: 2,
    title: 'Add Concentrated H2SO4',
    shortDescription: 'Add 2 drops of concentrated H2SO4 (in fume hood)',
    handler: 'handleAddH2SO4',
    validation: {
      requires: ['co3SolutionAdded'],
    },
    infoContent: {
      title: 'Carbonate-Acid Reaction',
      content: `Add **2 drops of concentrated H₂SO₄** (sulphuric acid). Carefully shake the tube and contents. The reaction is very fast so quickly notice the colour and odour of any gas given off. The presence of carbonates gives rise to the evolution of carbon dioxide (CO₂). Effervescence indicates the presence of carbon dioxide.`,
      chemicalEquation: 'CO3^2- + 2H+ -> H2O + CO2(g)',
      whatHappens: 'Concentrated sulfuric acid reacts with carbonate ions to rapidly produce carbon dioxide gas, water, and a sulfate salt.',
      observe: [
        'Vigorous effervescence (bubbling) occurs immediately upon acid addition',
        'The gas evolved is colorless and odorless CO2',
        'The reaction is very fast so observe carefully right after adding acid',
      ],
    },
  },
  {
    id: 3,
    title: 'Shake and Observe',
    shortDescription: 'Carefully shake the tube and observe gas evolution',
    handler: 'handleShakeCO3Tube',
    validation: {
      requires: ['h2so4Added'],
    },
  },
  {
    id: 4,
    title: 'Prepare Ba(OH)2 Confirmation',
    shortDescription: 'Add fresh carbonate solution to a new test tube',
    handler: 'handlePrepareBaOH2Test',
    validation: {
      requires: ['co3Effervescence'],
    },
  },
  {
    id: 5,
    title: 'Add HCl to New Tube',
    shortDescription: 'Add 2 drops of 6M HCl to generate CO2',
    handler: 'handleAddHClforBaOH2',
    validation: {
      requires: ['baoh2TestPrepared'],
    },
  },
  {
    id: 6,
    title: 'Position Ba(OH)2 Drop',
    shortDescription: 'Hold a drop of Ba(OH)2 solution above the tube opening',
    handler: 'handlePositionBaOH2Drop',
    validation: {
      requires: ['hclAddedForBaoh2'],
    },
    infoContent: {
      title: 'The Barium Carbonate Test',
      content: `Test the escaping gas for CO₂ by holding a drop of barium hydroxide solution suspended from an eye dropper above the surface of the solution. If the clear drop becomes cloudy, barium carbonate is present — confirming the presence of CO₂ and therefore the carbonate anion in solution.`,
      chemicalEquation: 'CO2 + Ba(OH)2 -> BaCO3 + H2O',
      whatHappens: 'CO2 gas rising from the acid-carbonate reaction reacts with the suspended Ba(OH)2 drop to form insoluble BaCO3, turning the clear drop cloudy white.',
      observe: [
        'Hold the Ba(OH)2 drop from an eye dropper just above the tube opening',
        'The clear drop turns cloudy white as BaCO3 forms in the suspended droplet',
        'Cloudiness confirms the escaping gas is CO2, proving the carbonate anion is present',
      ],
    },
  },
  {
    id: 7,
    title: 'Observe Drop Cloudiness',
    shortDescription: 'Observe if the clear drop becomes cloudy',
    handler: 'handleObserveBaOH2Drop',
    validation: {
      requires: ['baoh2DropPositioned'],
    },
  },
  {
    id: 8,
    title: 'Record Observation',
    shortDescription: 'Record the positive carbonate confirmation',
    handler: 'handleRecordCO3Result',
    validation: {
      requires: ['baoh2DropCloudy'],
    },
  },
];

// Part 3: Chloride and Iodide Detection
const part3Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Prepare Halide Test Tubes',
    shortDescription: 'Add 5 mL Cl- solution to tube 1, 5 mL I- solution to tube 2',
    handler: 'handlePrepareHalideTests',
    infoContent: {
      title: 'Silver Halide Precipitates',
      content: `Add **5 drops of 0.1M AgNO₃** (silver nitrate). If a precipitate forms then chloride and/or iodide anions are present. Silver chloride is a white precipitate; whereas silver iodide is yellow. Note the colour of the precipitate and try to determine whether iodide or chloride is present.`,
      chemicalEquation: 'Ag+ + X- -> AgX(s)',
      whatHappens: 'Silver nitrate reacts with halide ions to form insoluble silver halide precipitates whose colors differ: AgCl is white and AgI is yellow.',
      observe: [
        'AgCl forms as a white curdy precipitate in the chloride tube',
        'AgI forms as a distinctly yellow precipitate in the iodide tube',
        'Compare the two precipitate colors side by side to note the difference',
      ],
    },
  },
  {
    id: 2,
    title: 'Add AgNO3',
    shortDescription: 'Add 5 drops of 0.1M AgNO3 to each tube',
    handler: 'handleAddAgNO3',
    validation: {
      requires: ['halideTestsPrepared'],
    },
  },
  {
    id: 3,
    title: 'Observe Precipitate Colors',
    shortDescription: 'Compare the precipitate colors in each tube',
    handler: 'handleObserveHalidePrecipitates',
    validation: {
      requires: ['agno3Added'],
    },
  },
  {
    id: 4,
    title: 'Chloride Confirmation: Add NH4OH',
    shortDescription: 'Add concentrated ammonium hydroxide drop wise to AgCl precipitate',
    handler: 'handleAddNH4OHtoAgCl',
    validation: {
      requires: ['halidePrecipitatesObserved'],
    },
    infoContent: {
      title: 'Ammonia Dissolution of AgCl',
      content: `To the silver chloride precipitate add **concentrated ammonium hydroxide** drop wise. Part of the precipitate will dissolve:

AgCl(s) + 2NH₃(aq) -> [Ag(NH₃)₂]⁺(aq) + Cl⁻(aq)

Decant the clear supernatant solution and acidify it with drops of **6M nitric acid**. The reappearance of a white precipitate confirms the presence of the chloride ion.`,
      chemicalEquation: '[Ag(NH3)2]+ + Cl- + 2H+ -> AgCl(s) + 2NH4+',
      whatHappens: 'Ammonia dissolves the AgCl precipitate by forming the soluble diamminesilver(I) complex; acidifying the decanted solution re-precipitates AgCl, confirming chloride.',
      observe: [
        'White AgCl precipitate dissolves as concentrated NH4OH is added dropwise',
        'The supernatant becomes clear as the [Ag(NH3)2]+ complex forms',
        'Upon adding HNO3, the white AgCl precipitate reappears, confirming Cl-',
      ],
    },
  },
  {
    id: 5,
    title: 'Decant Clear Solution',
    shortDescription: 'Decant the clear supernatant to a new tube',
    handler: 'handleDecantAgNH3',
    validation: {
      requires: ['nh4ohAddedToAgCl'],
    },
  },
  {
    id: 6,
    title: 'Acidify with HNO3',
    shortDescription: 'Acidify with drops of 6M nitric acid, observe white precipitate reappearance',
    handler: 'handleAcidifyWithHNO3',
    validation: {
      requires: ['agnh3Decanted'],
    },
  },
  {
    id: 7,
    title: 'Record Chloride Result',
    shortDescription: 'Record the positive chloride confirmation',
    handler: 'handleRecordClResult',
    validation: {
      requires: ['agclReformed'],
    },
  },
  {
    id: 8,
    title: 'Iodide Confirmation: Prepare New Sample',
    shortDescription: 'Add 5 mL fresh I- solution to a new test tube',
    handler: 'handlePrepareIodideLiberation',
    validation: {
      requires: ['clResultRecorded'],
    },
    infoContent: {
      title: 'Iodine Liberation Test',
      content: `Add **5 mL** of the iodide solution to a test tube and add **dilute (6M) acetic acid** drop wise until the solution is acidic (confirm with litmus paper). Add **2 drops of 1M potassium nitrite (KNO₂)**. Upon the liberation of iodine, the solution turns reddish brown. Add **15 drops of methylene chloride**, shake and allow two layers to separate. A **violet colouration** in the methylene chloride layer confirms the presence of iodide.`,
      chemicalEquation: '2I- + 2NO2- + 4H+ -> I2 + 2NO + 2H2O',
      whatHappens: 'Nitrite ions oxidize iodide to molecular iodine (I2) in acidic solution; the liberated I2 is extracted into methylene chloride where it appears violet.',
      observe: [
        'The aqueous solution turns reddish-brown when I2 is liberated',
        'After adding methylene chloride and shaking, two layers separate',
        'The bottom organic layer turns violet/purple, confirming iodide presence',
        'The violet color is due to dissolved I2 in the organic solvent',
      ],
    },
  },
  {
    id: 9,
    title: 'Acidify with Acetic Acid',
    shortDescription: 'Add 6M acetic acid dropwise until acidic',
    handler: 'handleAddAceticAcid',
    validation: {
      requires: ['iodideLiberationPrepared'],
    },
  },
  {
    id: 10,
    title: 'Test pH with Litmus',
    shortDescription: 'Confirm solution is acidic',
    handler: 'handleTestIodidepH',
    validation: {
      requires: ['aceticAcidAdded'],
    },
  },
  {
    id: 11,
    title: 'Add KNO2',
    shortDescription: 'Add 2 drops of 1M potassium nitrite',
    handler: 'handleAddKNO2',
    validation: {
      requires: ['iodideSolutionAcidic'],
    },
  },
  {
    id: 12,
    title: 'Observe Color Change',
    shortDescription: 'Observe the reddish-brown color of liberated I2',
    handler: 'handleObserveI2Liberation',
    validation: {
      requires: ['kno2Added'],
    },
  },
  {
    id: 13,
    title: 'Add Methylene Chloride',
    shortDescription: 'Add 15 drops of methylene chloride',
    handler: 'handleAddMethyleneChloride',
    validation: {
      requires: ['i2Liberated'],
    },
  },
  {
    id: 14,
    title: 'Shake and Separate',
    shortDescription: 'Shake tube and allow layers to separate',
    handler: 'handleShakeAndSeparate',
    validation: {
      requires: ['methyleneChlorideAdded'],
    },
  },
  {
    id: 15,
    title: 'Observe Bottom Layer',
    shortDescription: 'Observe the violet/purple color in the organic layer',
    handler: 'handleObserveOrganicLayer',
    validation: {
      requires: ['layersSeparated'],
    },
  },
  {
    id: 16,
    title: 'Record Iodide Result',
    shortDescription: 'Record the positive iodide confirmation',
    handler: 'handleRecordIResult',
    validation: {
      requires: ['organicLayerObserved'],
    },
  },
];

// Part 4: Sulfate Confirmation Test
const part4Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Add SO4^2- Solution',
    shortDescription: 'Add 3 mL sulfate solution to a clean test tube',
    handler: 'handleAddSO4Solution',
    infoContent: {
      title: 'Sulfate Solution',
      content: `Sulfate solutions are colorless. The sulfate ion forms a highly insoluble precipitate with barium ions.`,
      whatHappens: 'A measured amount of colorless sulfate solution is placed in a clean test tube for the barium precipitation test.',
      observe: [
        'The sulfate solution is completely colorless',
        'Ensure the test tube is clean before adding the solution',
        'Prepare the water bath at 90C for the heating step',
      ],
    },
  },
  {
    id: 2,
    title: 'Add BaCl2-CaCl2 Mixture',
    shortDescription: 'Add 0.5 mL of BaCl2-CaCl2 mixture',
    handler: 'handleAddBaCl2CaCl2',
    validation: {
      requires: ['so4SolutionAdded'],
    },
    infoContent: {
      title: 'Barium Sulfate Precipitation',
      content: `Add **0.5 mL of the BaCl₂–CaCl₂** (barium chloride–calcium chloride) mixture. Heat to **90°C** in a water bath and allow to stand for about **10 minutes**. The appearance of a fine white precipitate (barium sulphate) indicates the presence of sulphate.

Add **5 drops of 6M HCl** to the white precipitate and heat the test tube in a **boiling water bath**. If none of the precipitate dissolves, the presence of sulphate is confirmed. BaSO₄ does NOT dissolve in HCl, unlike other barium precipitates (like BaCO₃ which dissolves in acid).`,
      chemicalEquation: 'Ba2+ + SO4^2- -> BaSO4(s)',
      whatHappens: 'Barium ions react with sulfate ions to form an extremely insoluble white BaSO4 precipitate that resists dissolution even in hot hydrochloric acid.',
      observe: [
        'A fine white precipitate of BaSO4 forms after heating in the water bath',
        'Allow the full 10 minutes for complete precipitation',
        'The precipitate does NOT dissolve when treated with HCl, confirming sulfate',
      ],
    },
  },
  {
    id: 3,
    title: 'Place in Water Bath',
    shortDescription: 'Place test tube in 90C water bath',
    handler: 'handlePlaceInWaterBath',
    validation: {
      requires: ['bacl2cacl2Added'],
    },
  },
  {
    id: 4,
    title: 'Wait 10 Minutes (Critical)',
    shortDescription: 'Heat for a FULL 10 minutes — do not rush this step',
    handler: 'handleWaitForPrecipitation',
    validation: {
      requires: ['tubeInWaterBath'],
    },
  },
  {
    id: 5,
    title: 'Observe White Precipitate',
    shortDescription: 'Observe the white BaSO4 precipitate',
    handler: 'handleObserveBaSO4',
    validation: {
      requires: ['heatingComplete'],
    },
  },
  {
    id: 6,
    title: 'Add HCl',
    shortDescription: 'Add 5 drops of 6M HCl to the precipitate',
    handler: 'handleAddHClToSO4',
    validation: {
      requires: ['baso4Observed'],
    },
  },
  {
    id: 7,
    title: 'Heat in Boiling Water Bath',
    shortDescription: 'Heat the tube in boiling water bath',
    handler: 'handleHeatWithHCl',
    validation: {
      requires: ['hclAddedToSO4'],
    },
  },
  {
    id: 8,
    title: 'Observe Precipitate Stability',
    shortDescription: 'Confirm the precipitate does NOT dissolve',
    handler: 'handleConfirmSO4',
    validation: {
      requires: ['heatedWithHCl'],
    },
  },
  {
    id: 9,
    title: 'Record Observation',
    shortDescription: 'Record the positive sulfate confirmation',
    handler: 'handleRecordSO4Result',
    validation: {
      requires: ['so4Confirmed'],
    },
  },
];

// Part 5: Unknown Identification
const part5Steps: ExperimentStep[] = [
  {
    id: 1,
    title: 'Receive Unknown',
    shortDescription: 'Obtain your unknown anion solution',
    handler: 'handleReceiveUnknown',
    infoContent: {
      title: 'Unknown Anion Identification',
      content: `Your unknown contains only **ONE** anion from: CO₃²⁻, Cl⁻, I⁻, SO₄²⁻. Use the confirmation tests you learned to systematically identify which anion is present.`,
      whatHappens: 'An unknown solution containing a single anion is provided for systematic identification using the confirmation tests learned in previous parts.',
      observe: [
        'Note whether the unknown solution has any color (iodide may be faintly yellow)',
        'Plan a systematic testing sequence: carbonate, halides, then sulfate',
        'Only one anion is present, so a positive result for one test completes the identification',
      ],
    },
  },
  {
    id: 2,
    title: 'Test for Carbonate',
    shortDescription: 'Perform the H2SO4 effervescence test',
    handler: 'handleTestUnknownCO3',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 3,
    title: 'Test for Halides',
    shortDescription: 'Perform the AgNO3 precipitation test',
    handler: 'handleTestUnknownHalide',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 4,
    title: 'Test for Sulfate',
    shortDescription: 'Perform the BaCl2 precipitation test',
    handler: 'handleTestUnknownSO4',
    validation: {
      requires: ['unknownReceived'],
    },
  },
  {
    id: 5,
    title: 'Submit Identification',
    shortDescription: 'Submit your identification of the unknown anion',
    handler: 'handleSubmitIdentification',
    validation: {
      requires: ['allUnknownTestsComplete'],
    },
  },
];

// Initial States
const part1InitialState = {
  safetyAcknowledged: false,
  anionsLearned: false,
  solutionsPrepared: false,
};

const part2InitialState = {
  co3SolutionAdded: false,
  h2so4Added: false,
  co3Effervescence: false,
  baoh2TestPrepared: false,
  hclAddedForBaoh2: false,
  baoh2DropPositioned: false,
  baoh2DropCloudy: false,
  co3Result: null as 'positive' | 'negative' | null,
};

const part3InitialState = {
  halideTestsPrepared: false,
  agno3Added: false,
  halidePrecipitatesObserved: false,
  clPrecipitateColor: '#FAFAFA',
  iPrecipitateColor: '#FFEB3B',
  nh4ohAddedToAgCl: false,
  agnh3Decanted: false,
  agclReformed: false,
  clResultRecorded: false,
  iodideLiberationPrepared: false,
  aceticAcidAdded: false,
  iodideSolutionAcidic: false,
  kno2Added: false,
  i2Liberated: false,
  methyleneChlorideAdded: false,
  layersSeparated: false,
  organicLayerObserved: false,
  organicLayerColor: '#7B1FA2',
  clResult: null as 'positive' | 'negative' | null,
  iResult: null as 'positive' | 'negative' | null,
};

const part4InitialState = {
  so4SolutionAdded: false,
  bacl2cacl2Added: false,
  tubeInWaterBath: false,
  heatingComplete: false,
  baso4Observed: false,
  hclAddedToSO4: false,
  heatedWithHCl: false,
  so4Confirmed: false,
  so4Result: null as 'positive' | 'negative' | null,
};

const part5InitialState = {
  unknownReceived: false,
  unknownAnion: null as string | null,
  unknownTests: {
    co3: null as 'positive' | 'negative' | null,
    halide: null as 'cl' | 'i' | 'negative' | null,
    so4: null as 'positive' | 'negative' | null,
  },
  allUnknownTestsComplete: false,
  submittedIdentification: null as string | null,
  isCorrect: null as boolean | null,
};

// Part Definitions
const part1: ExperimentPart = {
  id: 1,
  name: 'setup',
  title: 'Part I: Introduction & Setup',
  steps: part1Steps,
  equipment: ['Test tube rack', 'Test tubes', 'Labeled anion solutions'],
  initialState: part1InitialState,
};

const part2: ExperimentPart = {
  id: 2,
  name: 'carbonate',
  title: 'Part II: Carbonate (CO3^2-) Confirmation',
  steps: part2Steps,
  equipment: ['CO3^2- solution', 'Concentrated H2SO4', '6M HCl', 'Saturated Ba(OH)2 solution', 'Eyedropper'],
  initialState: part2InitialState,
};

const part3: ExperimentPart = {
  id: 3,
  name: 'halides',
  title: 'Part III: Chloride & Iodide Detection',
  steps: part3Steps,
  equipment: ['Cl- solution', 'I- solution', '0.1M AgNO3', 'Concentrated NH4OH', '6M HNO3', '6M Acetic acid', '1M KNO2', 'Methylene chloride', 'Litmus paper'],
  initialState: part3InitialState,
};

const part4: ExperimentPart = {
  id: 4,
  name: 'sulfate',
  title: 'Part IV: Sulfate (SO4^2-) Confirmation',
  steps: part4Steps,
  equipment: ['SO4^2- solution', 'BaCl2-CaCl2 mixture', 'Water bath (90C)', '6M HCl', 'Timer'],
  initialState: part4InitialState,
};

const part5: ExperimentPart = {
  id: 5,
  name: 'unknown',
  title: 'Part V: Unknown Anion Identification',
  steps: part5Steps,
  equipment: ['Unknown solution', 'All confirmation reagents'],
  initialState: part5InitialState,
};

// ============================================================================
// FULL EXPERIMENT CONFIGURATION
// ============================================================================

const experiment3Config: ExperimentConfig = {
  id: 'exp3',
  number: 3,
  name: 'Qualitative Analysis Part 2 - Anions',
  description: 'Identify anions (CO3^2-, Cl-, I-, SO4^2-) through confirmation tests using specific reagents, observing gas evolution, precipitate formation, color changes, and layer separation.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand the concept of anions and how they form',
    'Learn specific confirmation tests for CO3^2-, Cl-, I-, and SO4^2-',
    'Observe gas evolution (CO2) from carbonate-acid reactions',
    'Distinguish silver halide precipitates by color',
    'Understand oxidation of iodide and solvent extraction',
    'Recognize barium sulfate as an insoluble precipitate',
    'Apply systematic testing to identify unknown anions',
  ],
  safetyNotes: [
    'Wear safety goggles at all times',
    'Concentrated H₂SO₄ is very corrosive - handle with extreme care',
    'Work in fume hood when using H₂SO₄',
    'Methylene chloride is volatile - avoid inhalation',
    'Ba(OH)₂ is toxic - do not ingest',
    'AgNO₃ stains skin - avoid contact',
    'Wash hands thoroughly after the experiment',
    'Disposal: Sulphuric acid, hydrochloric acid, ammonium hydroxide, nitric acid, methylene chloride, acetic acid',
    'Iodine solution and silver nitrate will have their own disposal containers',
  ],
  requiredEquipment: [
    'Test tube rack',
    'Test tubes',
    'CO3^2- solution',
    'Cl- solution',
    'I- solution',
    'SO4^2- solution',
    'Concentrated H2SO4',
    '6M HCl',
    '6M HNO3',
    '6M Acetic acid',
    '0.1M AgNO3',
    'Concentrated NH4OH',
    '1M KNO2',
    'Methylene chloride',
    'BaCl2-CaCl2 mixture',
    'Saturated Ba(OH)₂ solution',
    'Water bath (90°C)',
    'Boiling water bath',
    'Beaker (250 mL, for decanting)',
    'Eye dropper',
    'Litmus paper',
    'Timer',
  ],
  parts: [part1, part2, part3, part4, part5],
  quiz: {
    experimentId: 'exp3',
    title: 'Anion Analysis Quiz',
    questions: [
      {
        id: 'exp3-q1',
        type: 'mcq' as const,
        question: 'How is the carbonate ion (CO₃\u00B2⁻) confirmed in qualitative analysis?',
        options: [
          { id: 'a', text: 'Adding AgNO₃ to form a colored precipitate' },
          { id: 'b', text: 'Adding acid and testing evolved gas with Ba(OH)₂ solution' },
          { id: 'c', text: 'Adding methylene chloride and observing color' },
          { id: 'd', text: 'Adding BaCl₂ directly to the solution' },
        ],
        correctOptionId: 'b',
        explanation: 'Acid reacts with CO₃\u00B2⁻ to produce CO₂ gas. The evolved CO₂ is passed into saturated Ba(OH)₂ solution, which turns cloudy white due to the formation of insoluble BaCO₃: CO₂ + Ba(OH)₂ → BaCO₃\u2193 + H₂O.',
      },
      {
        id: 'exp3-q2',
        type: 'mcq' as const,
        question: 'What color is AgI precipitate, and how does it differ from AgCl?',
        options: [
          { id: 'a', text: 'AgI is white; AgCl is yellow' },
          { id: 'b', text: 'AgI is yellow; AgCl is white' },
          { id: 'c', text: 'Both are white precipitates' },
          { id: 'd', text: 'Both are yellow precipitates' },
        ],
        correctOptionId: 'b',
        explanation: 'AgI forms a yellow precipitate while AgCl forms a white (curdy) precipitate. This color difference helps distinguish iodide from chloride ions using AgNO₃ reagent.',
      },
      {
        id: 'exp3-q3',
        type: 'mcq' as const,
        question: 'In the iodide confirmation test, what color does I₂ produce when extracted into methylene chloride (organic layer)?',
        options: [
          { id: 'a', text: 'Reddish-brown' },
          { id: 'b', text: 'Blue-black' },
          { id: 'c', text: 'Violet/purple' },
          { id: 'd', text: 'Colorless' },
        ],
        correctOptionId: 'c',
        explanation: 'Iodine (I₂) dissolves in organic solvents like methylene chloride to produce a characteristic violet/purple color. In water, I₂ appears reddish-brown. This difference in color between aqueous and organic layers confirms the presence of I⁻.',
      },
      {
        id: 'exp3-q4',
        type: 'short_answer' as const,
        question: 'Explain why BaSO₄ precipitate confirms the presence of sulfate ions, and why this precipitate is particularly useful as a confirmatory test.',
        sampleAnswer: 'When Ba\u00B2⁺ ions are added to a solution containing SO₄\u00B2⁻, barium sulfate (BaSO₄) forms as a white precipitate: Ba\u00B2⁺ + SO₄\u00B2⁻ → BaSO₄\u2193. BaSO₄ is one of the most insoluble compounds known (Ksp \u2248 1.1 × 10⁻\u00B9⁰), so it forms readily even at low sulfate concentrations. It is also insoluble in dilute acids, making it a reliable and unambiguous confirmation.',
      },
      {
        id: 'exp3-q5',
        type: 'calculation' as const,
        question: 'If 5.0 mL of 0.10 M Na₂SO₄ is mixed with excess BaCl₂, what mass of BaSO₄ precipitate (molar mass = 233.39 g/mol) should form?',
        expectedValue: 0.117,
        tolerance: 3,
        unit: 'g',
      },
    ],
  },
};

export default experiment3Config;
